import { DOCUMENT } from '@angular/common';
import { Directive, NgZone, Renderer2, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  debounceTime,
  filter,
  fromEvent,
  merge,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { WINDOW } from '../../cdk/browser';
import { elementResize } from '../../cdk/rxjs';
import { DropdownComponent } from './dropdown.component';

@Directive({
  selector: '[tvdDropdownPosition]',
})
export class DropdownPositionDirective {
  readonly #dropdown = inject(DropdownComponent);

  readonly #zone = inject(NgZone);
  readonly #docRef = inject(DOCUMENT);
  readonly #winRef = inject(WINDOW);
  readonly #renderer = inject(Renderer2);

  readonly #isOpen$ = toObservable(computed(() => this.#dropdown.opened()));
  readonly #open$ = this.#isOpen$.pipe(filter(Boolean));
  readonly #closed$ = this.#isOpen$.pipe(filter((open) => !open));

  readonly #run = this.#zone.runOutsideAngular(() => {
    this.#open$
      .pipe(switchMap(() => this.#updatePosition()))
      .subscribe(() => this.#determinePosition());
  });

  #updatePosition() {
    return merge(
      fromEvent(this.#docRef, 'scroll').pipe(takeUntil(this.#closed$)),
      fromEvent(this.#winRef, 'resize').pipe(
        debounceTime(200),
        takeUntil(this.#closed$),
      ),
      of(null),
      elementResize(this.#dropdown.container()),
    );
  }

  #determinePosition() {
    const anchorEl =
      this.#dropdown.attachTo() || this.#dropdown.anchor()?.nativeElement;
    if (!anchorEl) {
      return;
    }
    const anchorBox = anchorEl.getBoundingClientRect();
    const dropdown = this.#dropdown.container();
    const dropdownBox = dropdown.getBoundingClientRect();
    const offset = 1;
    const viewportWidth = this.#winRef.innerWidth;
    const viewportHeight = this.#winRef.innerHeight;

    // Ensure the dropdown can scroll if it cannot fully fit
    this.#renderer.setStyle(
      dropdown,
      'max-height',
      `${viewportHeight - 2 * offset}px`,
    );
    this.#renderer.setStyle(
      dropdown,
      'max-width',
      `${viewportWidth - 2 * offset}px`,
    );
    this.#renderer.setStyle(dropdown, 'overflow-y', 'auto');

    // Determine desired top position (prefer below, otherwise above)
    let top: number;
    const prefersBelow = dropdownBox.height + anchorBox.bottom < viewportHeight;
    if (prefersBelow) {
      top = anchorBox.bottom + offset;
    } else {
      top = anchorBox.top - dropdown.clientHeight - offset;
    }
    // Clamp top within viewport
    top = Math.max(
      offset,
      Math.min(top, viewportHeight - dropdown.clientHeight - offset),
    );
    this.#renderer.setStyle(dropdown, 'top', `${top}px`);
    // Expose placement for CSS animations
    this.#renderer.setAttribute(
      dropdown,
      'data-placement',
      prefersBelow ? 'below' : 'above',
    );

    // Determine desired left position (prefer align to left of anchor)
    let left: number;
    if (
      dropdownBox.width + anchorBox.right > viewportWidth &&
      anchorBox.left > viewportWidth - anchorBox.right
    ) {
      const width =
        dropdownBox.left -
        (dropdownBox.right - viewportWidth) -
        (viewportWidth - anchorBox.right);
      left = width;
    } else {
      left = anchorBox.left;
    }
    // Clamp left within viewport
    left = Math.max(
      offset,
      Math.min(left, viewportWidth - dropdown.clientWidth - offset),
    );
    this.#renderer.setStyle(dropdown, 'left', `${left}px`);
    if (this.#dropdown.customClasses()) {
      for (const className of this.#dropdown.customClasses().split(' ')) {
        this.#renderer.addClass(dropdown, className);
      }
    }
    if (this.#dropdown.alignWithAnchor()) {
      this.#renderer.setStyle(dropdown, 'width', `${anchorBox.width}px`);
    }
  }
}
